import { FC, useCallback, useState } from 'react'
import { FormDialog, Button } from '@mondra/ui-components'
import InputItem from '../../../../../components/InputItem'
import { useFormik } from 'formik'
import { elementaryFlowSchema } from '../utils'
import { addLocalPopUpNote } from '../../../../../shared/noteFunctions'

interface ElementaryFlowFormProps {
  resourceSources: any
  addProcess: (variables?: any, array?: string) => void
  // Which formik array this row is added to — 'elementaryFlows' (Inputs) or
  // 'outputElementaryFlows' (Outputs). Same modal shape, different section per D13a.
  targetArray: 'elementaryFlows' | 'outputElementaryFlows'
}

const initialValues = {
  resourceSourceId: 0,
  quantity: '',
  quantityNotes: [],
}

// Copy-adapted from EmissionToAirForm.tsx with elementary-flow labels, per FE spec §4.
const ElementaryFlowForm: FC<ElementaryFlowFormProps> = ({
  resourceSources,
  addProcess,
  targetArray,
}) => {
  const [openForm, setOpenForm] = useState(false)

  const formik = useFormik({
    initialValues,
    validationSchema: elementaryFlowSchema,
    onSubmit: values => {
      values.resourceSourceId = +values.resourceSourceId
      addProcess(values, targetArray)
      setTimeout(() => setOpenForm(false), 500)
    },
  })

  const onCreateElementaryFlow = useCallback(() => {
    setOpenForm(true)
    formik.setValues(initialValues)
    formik.setTouched({}, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClose = useCallback(() => {
    setTimeout(() => setOpenForm(false), 500)
  }, [])

  const addLocal = useCallback(
    params =>
      addLocalPopUpNote({
        formik,
        index: formik.values.quantityNotes.length,
        ...params,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.quantityNotes.length]
  )

  return (
    <>
      <Button
        title="Add elementary flow"
        iconType="add"
        variant="primary"
        className="mt-5"
        onClick={onCreateElementaryFlow}
      >
        Add elementary flow
      </Button>
      <FormDialog
        title="Add elementary flow"
        formId="component"
        onClose={onClose}
        primaryBtnText="Create"
        onPrimaryButtonClick={formik.handleSubmit}
        open={openForm}
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="piece-content-inputs">
            <InputItem
              name="Elementary flow type"
              description="Select the elementary flow. Science provides this list, filtered to entries flagged as both an emission and an elementary flow."
              type="select"
              create={true}
              formik={formik}
              showAsterisk
              onChange={formik.handleChange}
              formikName="resourceSourceId"
              defaultValue={formik.values.resourceSourceId ? formik.values.resourceSourceId : -1}
              options={resourceSources.map(el => ({
                label: el.combinedName,
                value: el.id,
              }))}
            />
            <InputItem
              name="Elementary flow ID"
              description=""
              type="select"
              create={true}
              formik={formik}
              showAsterisk
              onChange={formik.handleChange}
              formikName="resourceSourceId"
              defaultValue={formik.values.resourceSourceId ? formik.values.resourceSourceId : -1}
              options={resourceSources.map(el => ({
                label: el.combinedId,
                value: el.id,
              }))}
            />
            <InputItem
              name="Quantity"
              description="Please provide the quantity of the elementary flow in the displayed unit"
              type="input"
              formik={formik}
              onChange={formik.handleChange}
              defaultValue={formik.values.quantity}
              formikName="quantity"
              showAsterisk
              addonTitle={
                resourceSources.find(el => el.id === +formik.values.resourceSourceId)?.unit
              }
              addLocal={addLocal}
              notes={formik.values.quantityNotes || []}
            />
          </div>
        </form>
      </FormDialog>
    </>
  )
}

export default ElementaryFlowForm
